import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { RacePool, BettingInterest } from "@tvg/ts-types/Race";
import {
  Header,
  FilterCells,
  SegmentedControl,
  SegmentedControlTab,
  useMediaQuery,
  breakpoints
} from "@tvg/design-system";
import {
  PoolsEmptyStateCard,
  WagerTypePools,
  RunnersPools,
  ExoticNPicksPools
} from "./components";
import {
  PoolsContainer,
  ValueSwitcher,
  ButtonsWrapper,
  TableWrapper,
  Table,
  SegmentedControlValue,
  EmptyMessageContainer
} from "./styled-components";
import { getPoolsEmptyStateMessage } from "../../../store/selectors";

interface Props {
  wagerTypeLimit: number;
  racePools?: RacePool[] | null;
  bettingInterests?: BettingInterest[];
}

const PoolsCard = React.forwardRef<HTMLDivElement, Props>(
  ({ racePools = null, bettingInterests = [], wagerTypeLimit }, ref) => {
    const [currentUnit, setCurrentUnit] = useState("USD");
    const poolsEmptyMessage = useSelector(getPoolsEmptyStateMessage);

    const onlyExoticNPicksAvailable = useMemo(
      () =>
        racePools?.every((rp: RacePool) => rp.wagerType.id > wagerTypeLimit),
      [JSON.stringify(racePools)]
    );

    const isMobile = useMediaQuery(breakpoints.tablet.max.sm);

    if (!racePools) {
      return (
        <EmptyMessageContainer ref={ref}>
          <PoolsEmptyStateCard
            emptyTitle={poolsEmptyMessage.title}
            emptyMessage={poolsEmptyMessage.body}
          />
        </EmptyMessageContainer>
      );
    }
    return (
      <PoolsContainer ref={ref} data-qa-label="pools-container">
        <ValueSwitcher>
          {!onlyExoticNPicksAvailable && isMobile && (
            <SegmentedControl
              selectedValue={currentUnit}
              onChange={({ nativeEvent: { value } }) =>
                setCurrentUnit(value.toString())
              }
              qaLabel="card-type"
              size="s"
            >
              <SegmentedControlTab value="USD" qaLabel="card-type-credit">
                <SegmentedControlValue>$</SegmentedControlValue>
              </SegmentedControlTab>
              <SegmentedControlTab value="percentage" qaLabel="card-type-debit">
                <SegmentedControlValue>%</SegmentedControlValue>
              </SegmentedControlTab>
            </SegmentedControl>
          )}
          {!isMobile && (
            <>
              <Header qaLabel="pools-header" tag="h3" color="grey.900">
                Pools
              </Header>
              {!onlyExoticNPicksAvailable && (
                <ButtonsWrapper>
                  <FilterCells
                    filterOptions={[
                      { label: "$", value: "USD" },
                      { label: "%", value: "percentage" }
                    ]}
                    size="s"
                    bg="white.900"
                    activeValue={currentUnit}
                    qaLabel="pools-switcher"
                    hasShadow={true}
                    onChange={(unit) => setCurrentUnit(unit)}
                    minWidth={52}
                    hasWidth={false}
                  />
                </ButtonsWrapper>
              )}
            </>
          )}
        </ValueSwitcher>

        <TableWrapper>
          <Table>
            <WagerTypePools
              racePools={racePools}
              wagerTypeLimit={wagerTypeLimit}
            />
            <RunnersPools
              racePools={racePools}
              bettingInterests={bettingInterests}
              wagerTypeLimit={wagerTypeLimit}
              currentUnit={currentUnit}
            />
          </Table>
          <Table>
            <ExoticNPicksPools
              racePools={racePools}
              wagerTypeLimit={wagerTypeLimit}
            />
          </Table>
        </TableWrapper>
      </PoolsContainer>
    );
  }
);

export default PoolsCard;
